import React, { useEffect, useState } from 'react'
import { useLazyQuery } from '@apollo/client'
import { useStripe } from '@stripe/react-stripe-js'
import { useRouter } from 'next/router'
import { Button } from '@/atoms/Button'
import { paths, slugs } from '@/constants'
import { GET_ORDER_STATUS } from '@/services/NftService'
import { CollectibleProductResponse, PaymentMethod } from '@/services/NftService/types'
import { getPriceForCurrency } from '@/services/PriceFormatter'
import { useUser } from '@/services/UserService'
import { MoneyCurrencyCode } from '@/types/codegen-federation'
import { useSafeTrack } from '@/utils/analytics'
import { useEcommerceEvents } from '@/utils/analytics/ecommerce-events'
import SavedPaymentMethod from '../SavedPaymentMethod'

interface SavedPaymentMethodsProps {
  projectId: string
  paymentMethodsLoading?: boolean
  paymentMethods?: PaymentMethod[]
  defaultPaymentMethodId?: string
  selectedPaymentMethod?: string
  clientSecret?: string
  paymentIntentId?: string
  refetchCustomerPaymentMethods: () => void
  useNewPaymentMethod: () => void
  handlePaymentMethodSelection: (paymentMethodId: string) => void
  paymentIntentData?: CollectibleProductResponse
}

const SavedPaymentMethods: React.FC<SavedPaymentMethodsProps> = ({
  paymentMethodsLoading,
  paymentMethods = [],
  defaultPaymentMethodId,
  selectedPaymentMethod,
  clientSecret,
  paymentIntentId,
  refetchCustomerPaymentMethods,
  useNewPaymentMethod,
  handlePaymentMethodSelection,
  paymentIntentData,
  projectId,
}) => {
  const stripe = useStripe()
  const router = useRouter()

  const [isStripeLoading, setIsStripeLoading] = useState(false)
  const [isSelectingPaymentMethod, setIsSelectingPaymentMethod] = useState(false)
  const [savedPaymentMethodLoading, setSavePaymentMethodLoading] = useState(false)
  const { user } = useUser()
  const track = useSafeTrack()
  const { trackOrderCompleted } = useEcommerceEvents()

  const changeSavePaymentMethodLoading = (value: boolean) => {
    setSavePaymentMethodLoading(value)
  }

  const orderId = paymentIntentData?.node?.order?.id

  const [
    getOrderStatusQuery,
    { data: orderStatusData, loading: orderStatusLoading, error: orderStatusError, stopPolling },
  ] = useLazyQuery(GET_ORDER_STATUS, {
    variables: { orderId },
    pollInterval: 1000,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  })

  useEffect(() => {
    if (orderStatusData && orderStatusData?.order?.status !== 'PENDING') {
      stopPolling()
      router.push(`${paths.collectibles.success(projectId)}?order=${orderId}`)
    }
  }, [orderStatusData])

  const handleCompletePurchase = async () => {
    if (!stripe) {
      return
    }

    setIsStripeLoading(true)

    const { paymentIntent: paymentReceipt } = await stripe.confirmCardPayment(clientSecret as string)

    track('Scene Pack Complete Purchase Clicked', {
      purchaseMethod: 'credit-card',
      projectSlug: projectId,
    })

    if (paymentReceipt) {
      const prePaymentIntent = paymentIntentData?.node
      trackOrderCompleted({
        currency: paymentReceipt.currency as MoneyCurrencyCode,
        email: paymentReceipt.receipt_email || user?.email || '',
        funnel: 'collectibles',
        order_id: paymentReceipt.id,
        total: getPriceForCurrency(paymentReceipt.amount, paymentReceipt.currency),
        projectSlug: projectId,
        checkout_id: orderId,
        tax: prePaymentIntent?.tax && getPriceForCurrency(prePaymentIntent.tax, paymentReceipt.currency),
        subtotal: prePaymentIntent?.price && getPriceForCurrency(prePaymentIntent.price, paymentReceipt.currency),
        products: [
          {
            category: 'collectibles',
            name: paymentReceipt.description || '',
            price: getPriceForCurrency(paymentReceipt.amount, paymentReceipt.currency),
            currency: paymentReceipt.currency as MoneyCurrencyCode,
            quantity: 1,
            product_id: paymentReceipt.id,
            sku: paymentReceipt.id,
          },
        ],
      })
      getOrderStatusQuery()
    }
  }

  useEffect(() => {
    if (!stripe) {
      return
    }

    const clientSecret = new URLSearchParams(window.location.search).get('payment_intent_client_secret')

    if (!clientSecret) {
      return
    }
  }, [stripe])

  return (
    <>
      {!paymentMethodsLoading && (
        <div className="flex flex-col justify-center gap-y-2">
          <div className="w-full flex flex-col gap-y-2">
            {paymentMethods?.map(({ id, brand, expYear, last4 }, idx) => (
              <SavedPaymentMethod
                key={id}
                paymentMethodId={id}
                paymentIntentId={paymentIntentId}
                brand={brand}
                last4={last4}
                expYear={expYear}
                selectedCard={selectedPaymentMethod}
                isSelectingPaymentMethod={isSelectingPaymentMethod}
                isDefaultPaymentMethod={defaultPaymentMethodId === id}
                isLastCard={idx === paymentMethods.length - 1}
                savedPaymentMethodLoading={savedPaymentMethodLoading}
                handlePaymentMethodSelection={handlePaymentMethodSelection}
                closePaymentMethodSelection={() => setIsSelectingPaymentMethod(false)}
                changeSavePaymentMethodLoading={changeSavePaymentMethodLoading}
                refetchCustomerPaymentMethods={refetchCustomerPaymentMethods}
              />
            ))}
            {!isSelectingPaymentMethod && paymentMethods?.length > 0 && (
              <div className="mb-4 flex flex-wrap gap-2">
                <Button variant="gray" className="text-xs p-2" onClick={useNewPaymentMethod}>
                  New payment method
                </Button>
                <Button variant="gray" className="text-xs p-2" onClick={() => setIsSelectingPaymentMethod(true)}>
                  Change payment method
                </Button>
              </div>
            )}
          </div>

          {!isSelectingPaymentMethod && paymentMethods?.length > 0 && (
            <div className="w-full flex flex-row items-center">
              {isStripeLoading ? (
                <div className="text-black rounded-lg border-black w-full px-6 py-3 border">Processing...</div>
              ) : (
                <Button
                  className="text-black w-full py-3"
                  variant="oxide"
                  disabled={isStripeLoading}
                  id="submit"
                  type="submit"
                  onClick={handleCompletePurchase}
                >
                  Complete Purchase
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default SavedPaymentMethods
